import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, MapPin, Globe } from 'lucide-react';
import { teamMembers, owners } from '../mock';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAgent } from '../context/AgentContext';
import { TeamPhoto } from '../components/TeamPhoto';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations for Team page
const pageText = {
  en: {
    hero: {
      title: 'Our Team',
      subtitle: 'Meet the experts dedicated to your financial success'
    },
    managingPartners: {
      title: 'Managing Partners',
      subtitle: 'Leadership behind Utmost Financial Inc.'
    },
    insuranceTeam: {
      title: 'Insurance & Investments Team',
      description: 'Dedicated advisors for segregated funds and insurance solutions'
    },
    mortgageTeam: {
      title: 'Mortgage Team',
      description: 'Expert mortgage brokers helping you find the best rates and solutions'
    },
    cta: {
      title: 'Want to Join Our Team?',
      subtitle: "We're always looking for talented professionals to join Utmost Financial.",
      button: 'Contact Us'
    },
    visitWebsite: 'Visit Website',
    website: 'Website'
  },
  fr: {
    hero: {
      title: 'Notre Équipe',
      subtitle: 'Rencontrez les experts dédiés à votre succès financier'
    },
    managingPartners: {
      title: 'Associés Directeurs',
      subtitle: 'Le leadership derrière Utmost Financial Inc.'
    },
    insuranceTeam: {
      title: 'Équipe Assurance & Investissements',
      description: 'Conseillers dévoués pour les fonds distincts et solutions d\'assurance'
    },
    mortgageTeam: {
      title: 'Équipe Hypothécaire',
      description: 'Courtiers hypothécaires experts vous aidant à trouver les meilleurs taux et solutions'
    },
    cta: {
      title: 'Vous Voulez Rejoindre Notre Équipe?',
      subtitle: 'Nous recherchons toujours des professionnels talentueux pour rejoindre Utmost Financial.',
      button: 'Contactez-Nous'
    },
    visitWebsite: 'Visiter le Site',
    website: 'Site Web'
  }
};

const TeamPage = () => {
  const { language } = useLanguage();
  const { buildLink } = useAgent();
  const text = pageText[language];
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch all agents from API
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await fetch(`${API_URL}/api/agents`);
        if (response.ok) {
          const data = await response.json();
          setAllAgents(data);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);
  
  // Separate agents by role
  const apiInsuranceTeam = allAgents.filter(a => 
    a.role?.includes('Insurance') || a.role?.includes('Seg Funds')
  );
  const apiMortgageTeam = allAgents.filter(a => 
    a.role?.includes('Mortgage') || a.role?.includes('Broker')
  );
  const apiOwners = allAgents.filter(a => 
    a.role?.includes('Managing Partner') || a.role?.includes('Principal')
  );
  const apiMortgageAgents = apiMortgageTeam.filter(a => 
    !a.role?.includes('Managing Partner') && !a.role?.includes('Principal')
  );
  
  // Use API data if available, otherwise fallback to mock data
  const mockInsuranceTeam = teamMembers.filter(member => member.category === 'insurance');
  const mockMortgageTeam = teamMembers.filter(member => member.category === 'mortgage');
  
  const insuranceTeam = apiInsuranceTeam.length > 0 ? apiInsuranceTeam : mockInsuranceTeam;
  const mortgageTeam = apiMortgageAgents.length > 0 ? apiMortgageAgents : mockMortgageTeam;
  const ownersTeam = apiOwners.length > 0 ? apiOwners : owners;

  // Owner Card Component (larger size for Managing Partners)
  const OwnerCard = ({ member }) => {
    const memberImage = member.photo || member.image;
    const memberWebsite = member.slug ? `/agent/${member.slug}` : member.website;
    const hasAgentWebsite = !!member.slug;
    
    return (
      <Card className="hover:shadow-xl transition-all border-2 border-[#D4AF37] bg-white w-[280px]">
        <CardContent className="p-0">
          <div className="h-[280px] overflow-hidden bg-gray-100">
            <TeamPhoto src={memberImage} name={member.name} className="w-full h-full object-cover object-top" />
          </div>
          <div className="p-5 bg-white">
            <h3 className="text-lg font-bold text-gray-800 mb-1">{member.name}</h3>
            <p className="text-sm text-[#0077B6] font-bold mb-3">{member.role}</p>
            <div className="flex items-center text-sm text-[#D4AF37] mb-3">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="font-bold">{member.provinces}</span>
            </div>
            <div className="flex flex-col space-y-2 text-sm">
              <a
                href={`tel:${member.phone.replace(/[^0-9+]/g, '')}`}
                className="flex items-center text-gray-700 hover:text-[#0077B6] transition-colors"
              >
                <Phone className="h-4 w-4 mr-2 flex-shrink-0 text-[#0077B6]" />
                <span>{member.phone}</span>
              </a>
              <a
                href={`mailto:${member.email}`}
                className="flex items-center text-gray-700 hover:text-[#0077B6] transition-colors"
              >
                <Mail className="h-4 w-4 mr-2 flex-shrink-0 text-[#0077B6]" />
                <span className="truncate">{member.email}</span>
              </a>
              {memberWebsite && (
                <a
                  href={memberWebsite}
                  target={hasAgentWebsite ? "_self" : "_blank"}
                  rel={hasAgentWebsite ? "" : "noopener noreferrer"}
                  className="flex items-center text-[#0077B6] hover:text-[#005A8C] transition-colors font-bold"
                >
                  <Globe className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>{hasAgentWebsite ? "My Website" : text.visitWebsite}</span>
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const TeamSection = ({ title, members, description, isInsuranceTeam = false }) => (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">{title}</h2>
        {description && <p className="text-gray-600 text-lg">{description}</p>}
        <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-3"></div>
      </div>
      <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
        {members.map((member) => {
          // Handle both API agent data and mock data
          const memberImage = member.photo || member.image;
          const memberWebsite = member.slug ? `/agent/${member.slug}` : member.website;
          const hasAgentWebsite = !!member.slug;
          
          return (
            <Card key={member.id} className="hover:shadow-lg transition-all w-[180px] bg-white border border-gray-200 hover:border-[#0077B6]">
              <CardContent className="p-0">
                <div className="h-[200px] overflow-hidden bg-gray-100">
                  <TeamPhoto src={memberImage} name={member.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-3 bg-white">
                  <h3 className="text-sm font-bold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-sm text-[#0077B6] font-bold mb-1">{member.role}</p>
                  <div className="flex items-center text-sm text-gray-600 mb-1">
                    <MapPin className="h-3 w-3 mr-1 flex-shrink-0 text-[#D4AF37]" />
                    <span>{member.provinces}</span>
                  </div>
                  <div className="flex flex-col space-y-1 text-sm">
                    <a
                      href={`tel:${member.phone.replace(/[^0-9+]/g, '')}`}
                      className="flex items-center text-gray-600 hover:text-[#0077B6] transition-colors"
                    >
                      <Phone className="h-3 w-3 mr-1 flex-shrink-0 text-[#0077B6]" />
                      <span className="truncate">{member.phone}</span>
                    </a>
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center text-gray-600 hover:text-[#0077B6] transition-colors"
                    >
                      <Mail className="h-3 w-3 mr-1 flex-shrink-0 text-[#0077B6]" />
                      <span className="truncate">{member.email}</span>
                    </a>
                    {memberWebsite && (
                      <a
                        href={memberWebsite}
                        target={hasAgentWebsite ? "_self" : "_blank"}
                        rel={hasAgentWebsite ? "" : "noopener noreferrer"}
                        className="flex items-center text-[#0077B6] hover:text-[#005A8C] transition-colors font-bold"
                      >
                        <Globe className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span>{hasAgentWebsite ? "My Website" : text.website}</span>
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">{text.hero.title}</h1>
          <p className="text-lg text-blue-100">
            {text.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Managing Partners Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{text.managingPartners.title}</h2>
            <p className="text-gray-600">{text.managingPartners.subtitle}</p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-3"></div>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {ownersTeam.map((owner) => (
              <OwnerCard key={owner.id} member={owner} />
            ))}
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Insurance & Investments Team */}
          <TeamSection 
            title={text.insuranceTeam.title}
            members={insuranceTeam}
            description={text.insuranceTeam.description}
          />
          
          {/* Mortgage Team */}
          <TeamSection 
            title={text.mortgageTeam.title}
            members={mortgageTeam}
            description={text.mortgageTeam.description}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{text.cta.title}</h2>
          <p className="text-blue-100 mb-6">
            {text.cta.subtitle}
          </p>
          <Button asChild className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-semibold">
            <a href="/contact">{text.cta.button}</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
